import DTODashBoardResultSet = namaTypes.DTODashBoardResultSet;
import DTOUserDashbordChartLine = namaTypes.DTOUserDashbordChartLine;
import DTOBIChartLine = namaTypes.DTOBIChartLine;
import DTODashboardValue = namaTypes.DTODashboardValue;
import DashboardConstants from "@/utils/constants/DashboardConstants.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import Translator from "@/utils/common/Translator.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {EventInput} from "@fullcalendar/core";
import type {ResourceInput} from "@fullcalendar/resource";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {Dialog} from "quasar";
import QuickEditReferenceDialog from "@/components/EditView/QuickEditReferenceDialog.vue";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";

export default class DashboardUtils {

  static geCSVColumns(row: number, csvColumnNames: string, resultSet: DTODashBoardResultSet): string[] {
    let columnNames = StringUtils.csvLineToList(csvColumnNames, ",");
    let values: string[] = [];
    for (let columnName of columnNames) {
      values.push(this.getByColName(row, columnName, resultSet));
    }
    return values;
  }

  static addLinkOptionsToPoint(params: Record<any, any>, list: DTODashBoardResultSet, i: number, point: Record<any, any>) {
    this.addOptionToPoint(params, DashboardConstants.Param_Link, list, point, i);
    this.addOptionToPoint(params, DashboardConstants.Param_LinkToID, list, point, i);
    this.addOptionToPoint(params, DashboardConstants.Param_LinkToType, list, point, i);
  }

  private static addOptionToPoint(params: Record<any, any>, paramName: string, list: DTODashBoardResultSet, point: Record<any, any>, i: number) {
    let value: string = this.getByColName(i, params[paramName], list);
    if (!value)
      return;
    point[paramName] = value;
  }

  static getByColName(row: number, columnName: string, resultSet: DTODashBoardResultSet): string {
    const valuesOfFirstRow = resultSet?.rows?.[0]?.values;
    if (ObjectChecker.isEmptyOrNull(valuesOfFirstRow))
      return "";
    const index: number = valuesOfFirstRow.findIndex(v => ObjectChecker.areEqual(v, this.createDashboardValue(columnName)));
    if (index < 0)
      return "";
    return this.getByIndex(row, index, resultSet);
  }

  static createDashboardValue(value: string | boolean | number): DTODashboardValue {
    let object: DTODashboardValue = {};
    if (ObjectChecker.isNumber(value))
      object.d = value;
    else if (ObjectChecker.isBoolean(value))
      object.b = value;
    else
      object.s = value;
    return object;
  }

  static getByIndex(row: number, index: number, resultSet: DTODashBoardResultSet): string {
    return this.get(row, resultSet)[index];
  }

  private static get(i: number, resultSet: DTODashBoardResultSet): string[] {
    if (!resultSet.rows || !resultSet.rows.length || i + 1 >= resultSet?.rows?.length)
      return [];
    const dtoDashboardValues = resultSet?.rows[i + 1].values ?? [];
    return dtoDashboardValues.map(DashboardUtils.getDashboardValue).map(String);
  }

  static handleReferenceQuickEdit(link: string, event: MouseEvent) {
    if (event.ctrlKey)
      return;
    const openedAsQuickEditDialog = this.tryToOpenQuickEditReferenceDialog(link);
    if (openedAsQuickEditDialog)
      event.preventDefault();
  }

  static tryToOpenQuickEditReferenceDialog(link: string) {
    if (!this.canOpenQuickEditReferenceDialog(link))
      return false;
    this.openQuickEditReferenceDialog(link);
    return true;
  }

  static openQuickEditReferenceDialog(linkOrEditPlace: string | EditPlace) {
    let editPlace = linkOrEditPlace;
    if (ObjectChecker.isString(linkOrEditPlace))
      editPlace = new EditPlace(PlaceUtils.getQueryFromUrl(PlaceUtils.normalizedGUILink(linkOrEditPlace)));
    Dialog.create({
      component: QuickEditReferenceDialog,
      componentProps: {
        editPlace
      }
    });
  }

  static canOpenQuickEditReferenceDialog(link?: string) {
    if (ObjectChecker.isEmptyOrNull(link))
      return false;
    const normalizedLink = PlaceUtils.normalizedGUILink(link);
    if (!normalizedLink?.includes("/edit"))
      return false;
    const query = PlaceUtils.getQueryFromUrl(normalizedLink);
    if (ObjectChecker.isEmptyOrNull(query))
      return false;
    else
      return query[PlaceTokens.PRM_ENTITY_TYPE] && (query[PlaceTokens.PRM_ID] || query[PlaceTokens.PRM_FIELDS]);
  }

  static async handleChartClick(object: any) {
    let options: any = object
    if (options.point)
      options = options.point;
    if (!options)
      return;
    let link = options[DashboardConstants.Param_Link];
    const linkToId = options[DashboardConstants.Param_LinkToID];
    const linkToType = options[DashboardConstants.Param_LinkToType];
    if (link) {
      if (link.includes("//")) {
        if (object.ctrlKey || !this.canOpenQuickEditReferenceDialog(link))
          window.open(link, "_blank", "");
        else if (object.altKey)
          GUIUtilities.goToInternalURL(link);
        else
          this.openQuickEditReferenceDialog(link);
      }
      else
        GUIUtilities.goToInternalURL(link);
    } else if (linkToType && linkToId) {
      const editPlace = PlaceUtils.editPlace(linkToType, linkToId)!;
      const editLink = editPlace.toLink();
      if (object.ctrlKey)
        window.open(editLink, "_blank", "");
      else if (object.altKey)
        GUIUtilities.goToInternalURL(editLink);
      else
        DashboardUtils.openQuickEditReferenceDialog(editPlace);
    }
  }

  static addDataLine(categs: string[], labelWithValues: Record<string, any>, category: string, label: string, value: string, params: Record<string, any>, values: DTODashBoardResultSet, i: number) {
    if (!categs.contains(category))
      categs.push(category);
    if (!labelWithValues[label])
      labelWithValues[label] = [];
    let point = {y: parseFloat(value)};
    this.addLinkOptionsToPoint(params, values, i, point);
    labelWithValues[label].push(point);

  }

  static calcChartTitle(chartLine: DTOUserDashbordChartLine): string {
    const isAr = Translator.isArabic();
    const primary = isAr ? chartLine.arChartTitle : chartLine.enChartTitle;
    if (primary) return primary;
    const bi = chartLine as DTOBIChartLine;
    const widgetName = isAr ? bi.widgetName1 : bi.widgetName2;
    if (widgetName) return widgetName;
    if (bi.widgetCode) return bi.widgetCode;
    return '';
  }

  static constructCategLabelsValues(userDashboardChartLine: DTOUserDashbordChartLine, categories: string[]): Record<any, any> {
    let params: Record<any, any> = userDashboardChartLine.parameters ?? {};
    let resultSetValues: DTODashBoardResultSet = userDashboardChartLine.resultSet ?? {};
    let labelParam: any = params[DashboardConstants.Param_Label];
    let valueParam: any = params[DashboardConstants.Param_Value];
    let categParam: any = params[DashboardConstants.Param_Category];
    let categsWithData: Record<any, any> = {};
    for (let i = 0; i < this.resultSetSize(resultSetValues); i++) {
      let label: string = this.getByColName(i, labelParam, resultSetValues);
      let value: string = this.getByColName(i, valueParam, resultSetValues);
      let categ: string = this.getByColName(i, categParam, resultSetValues);
      if (!categsWithData[categ])
        categsWithData[categ] = [];
      let point: Record<any, any> = {y: parseFloat(value)};
      this.addLinkOptionsToPoint(params, resultSetValues, i, point);
      categsWithData[categ].push(point);
      if (!categories.includes(label))
        categories.push(label);
    }
    return categsWithData;
  }

  static constructMapOfLabelsValues(userDashboardChartLine: DTOUserDashbordChartLine): Record<any, any> {
    let params: Record<any, any> = userDashboardChartLine.parameters ?? {};
    let resultSetValues: DTODashBoardResultSet = userDashboardChartLine.resultSet ?? {};
    let labelWithValues: Record<any, any> = {};
    let labelParam: any = params[DashboardConstants.Param_Label];
    let valueParam: any = params[DashboardConstants.Param_Value];
    for (let i = 0; i < this.resultSetSize(resultSetValues); i++) {
      let label: string = this.getByColName(i, labelParam, resultSetValues);
      let value: string = this.getByColName(i, valueParam, resultSetValues);
      if (!labelWithValues[label])
        labelWithValues[label] = [];
      let point: Record<any, any> = {y: parseFloat(value)};
      this.addLinkOptionsToPoint(params, resultSetValues, i, point);
      labelWithValues[label].push(point);
    }
    return labelWithValues;
  }

  static resultSetSize(resultSet: DTODashBoardResultSet): number {
    return ((resultSet.rows?.length ?? 0) - 1);
  }

  static createEventsAndResources(data: DTOUserDashbordChartLine, events: EventInput[], resources: ResourceInput []) {
    let resourceIds: Record<string, any> = {};
    let resultSetValues: DTODashBoardResultSet = data.resultSet ?? {};
    for (let i = 0; i < this.resultSetSize(resultSetValues); i++) {
      let event: EventInput = this.createEvent(data, i);
      events.push(event);
      let parameters: Record<any, any> = data.parameters ?? {};
      let resId = this.getByColName(i, parameters[DashboardConstants.Param_ResourceID], resultSetValues);
      if (resId && !resourceIds[resId]) {
        resources.push(this.createResource(data, i));
        resourceIds[resId] = true;
      }
    }
  }

  private static createResource(data: DTOUserDashbordChartLine, i: number): ResourceInput {
    let resource: ResourceInput = {};
    let parameters: Record<any, any> = data.parameters ?? {};
    let resultSetValue = data.resultSet ?? {};
    resource.title = this.getByColName(i, parameters[DashboardConstants.Param_ResourceName], resultSetValue);
    resource.id = this.getByColName(i, parameters[DashboardConstants.Param_ResourceID], resultSetValue);
    if (!resource.id)
      resource.id = resource.title;
    resource.parentId = this.getByColName(i, parameters[DashboardConstants.Param_ParentResourceID], resultSetValue);
    return resource;
  }

  private static createEvent(data: DTOUserDashbordChartLine, i: number): EventInput {
    let event: EventInput = {};
    let parameters: Record<any, any> = data.parameters ?? {};
    let resultSetValue: DTODashBoardResultSet = data.resultSet ?? {};
    event.title = this.getByColName(i, parameters[DashboardConstants.Param_Title], resultSetValue);
    event.eventColor = this.getByColName(i, parameters[DashboardConstants.Param_Color], resultSetValue);
    event.start  = this.getTime(data, i, "From");
    event.end = this.getTime(data, i, "To");
    if (event.end && event.start && (event.end as Date).getFullYear() < (event.start as Date).getFullYear()) {
      event.end = event.start;
      event.allDay = true;
    }
    event.resourceId = this.getByColName(i, parameters[DashboardConstants.Param_ResourceID], resultSetValue);
    if (parameters[DashboardConstants.Param_LinkToType] && parameters[DashboardConstants.Param_LinkToID]) {
      let linkToType = this.getByColName(i, parameters[DashboardConstants.Param_LinkToType], resultSetValue);
      let linkToID = this.getByColName(i, parameters[DashboardConstants.Param_LinkToID], resultSetValue);
      let editPlace: EditPlace = new EditPlace();
      editPlace.entityType = linkToType;
      editPlace.id = linkToID;
      editPlace.view = ViewMode.EDIT;
      event.url = PlaceUtils.linkToPlace(editPlace);
      event.context = {
        editPlace
      }
    } else {
      event.url = this.getByColName(i, parameters[DashboardConstants.Param_URL], resultSetValue);
    }
    return event;
  }

  private static getTime(data: DTOUserDashbordChartLine, i: number, prefix: string): Date | undefined {
    let parameters: Record<any, any> = data.parameters ?? {};
    let dateTimeColName: string = parameters[prefix + "DateTime"];
    if (dateTimeColName)
      return this.getTimeFrom(dateTimeColName, "yyyy-MM-dd HH:mm:ss.SSS", data, i)!;
    let dateColName: string = parameters[prefix + "Date"];
    let timeColName: string = parameters[prefix + "Time"];
    let date: Date | undefined = this.getTimeFrom(dateColName, "yyyy-MM-dd", data, i);
    let time: Date | undefined = this.getTimeFrom(timeColName, "i", data, i);
    return new Date(this.toTimeMS(date) + this.toTimeMS(time));
  }

  private static toTimeMS(date: Date | undefined): number {
    if (!date)
      return 0;
    return date.getTime();
  }

  private static getTimeFrom(key: string, defaultFormat: string, data: DTOUserDashbordChartLine, i: number): Date | undefined {
    let formatStr: string = defaultFormat;
    if (key?.includes("{")) {
      formatStr = StringUtils.getInnerString(key, "{", "}") ?? '';
      key = key.replaceAll("{" + key + "}", "");
    }
    let value: string = this.getByColName(i, key, data.resultSet ?? {});
    if (!value)
      return undefined;
    if ("i" === formatStr) {
      let d: Date = new Date();
      d.setTime(parseFloat(value));
      return d;
    }
    return new Date(value);
  }

  static makeATree(resources: ResourceInput[]): ResourceInput[] {
    let roots: ResourceInput[] = [];
    let map: Record<string, ResourceInput> = {};
    for (let res of resources) {
      if (res.id)
        map[res.id] = res;
      if (!res.parentId)
        roots.push(res);
    }
    for (let res of resources) {
      if (res.parentId) {
        let parent: any = map[res.parentId];
        if (!parent) {
          parent = {};
          roots.push(parent);
          parent.id = res.parentId;
          parent.title = res.parentId;
          map[parent.id] = parent;
        }
        if (!parent.children)
          parent.children = [];
        parent.children.push(res);
      }
    }
    return roots;
  }

  private static getDashboardValue(dto: DTODashboardValue): string | number | boolean {
    return ObjectChecker.getFirstNotNullObj(dto.d, dto.b, ObjectChecker.toStringOrEmpty(dto.s));
  }
}
